import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Input } from "../../../components/ui/input";
import { Table, TBody, Td, THead, Th, Tr } from "../../../components/ui/table";
import { api, qk, unwrap } from "../../../lib/api";
import { formatBytes } from "../../../lib/utils";

export const Route = createFileRoute("/_app/admin/usage")({ component: Usage });

function Usage() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const q = useQuery({
    queryKey: qk.admin.usage(month),
    queryFn: () => unwrap(api.admin.usage.$get({ query: { month } })),
  });
  const total = (q.data?.usage ?? []).reduce((s, r) => s + Number(r.costUsd), 0);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="w-44"
          aria-label="Month"
        />
        <span className="text-sm text-muted-foreground">
          AI cost this month: <strong className="text-foreground">${total.toFixed(4)}</strong> (from
          ai_usage)
        </span>
      </div>
      <Table>
        <THead>
          <Tr>
            <Th>Organization</Th>
            <Th>Client</Th>
            <Th className="text-right">Calls</Th>
            <Th className="text-right">Input tokens</Th>
            <Th className="text-right">Output tokens</Th>
            <Th className="text-right">Web searches</Th>
            <Th className="text-right">Cost (USD)</Th>
          </Tr>
        </THead>
        <TBody>
          {q.data?.usage.length === 0 && (
            <Tr>
              <Td colSpan={7} className="text-center text-muted-foreground">
                No AI calls in {month}.
              </Td>
            </Tr>
          )}
          {q.data?.usage.map((r) => (
            <Tr key={`${r.orgId}-${r.clientId}`}>
              <Td>{r.orgName}</Td>
              <Td>{r.clientName ?? "—"}</Td>
              <Td className="text-right tabular-nums">{r.calls}</Td>
              <Td className="text-right tabular-nums">{r.inputTokens.toLocaleString()}</Td>
              <Td className="text-right tabular-nums">{r.outputTokens.toLocaleString()}</Td>
              <Td className="text-right tabular-nums">{r.searchQueries.toLocaleString()}</Td>
              <Td className="text-right tabular-nums">{Number(r.costUsd).toFixed(4)}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      {!!q.data?.steps.length && (
        <>
          <h2 className="text-sm font-medium text-muted-foreground">Per pipeline step</h2>
          <Table>
            <THead>
              <Tr>
                <Th>Step</Th>
                <Th className="text-right">Calls</Th>
                <Th className="text-right">Input tokens</Th>
                <Th className="text-right">Output tokens</Th>
                <Th className="text-right">Web searches</Th>
                <Th className="text-right">Cost (USD)</Th>
              </Tr>
            </THead>
            <TBody>
              {q.data.steps.map((r) => (
                <Tr key={r.step}>
                  <Td>{r.step}</Td>
                  <Td className="text-right tabular-nums">{r.calls}</Td>
                  <Td className="text-right tabular-nums">{r.inputTokens.toLocaleString()}</Td>
                  <Td className="text-right tabular-nums">{r.outputTokens.toLocaleString()}</Td>
                  <Td className="text-right tabular-nums">{r.searchQueries.toLocaleString()}</Td>
                  <Td className="text-right tabular-nums">{Number(r.costUsd).toFixed(4)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </>
      )}
      <h2 className="text-sm font-medium text-muted-foreground">
        Items and storage per organization
      </h2>
      <Table>
        <TBody>
          {q.data?.orgs.map((o) => (
            <Tr key={o.id}>
              <Td>{o.name}</Td>
              <Td className="text-right tabular-nums">{o.itemCount} items</Td>
              <Td className="text-right tabular-nums">{formatBytes(o.storageBytes)}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
